import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Axios from "axios";
import { Grid, Typography, CircularProgress, Container, Box, Button, Card, CardContent, Divider, Avatar, Tabs, Tab } from "@mui/material";
import { useTranslation } from 'react-i18next';
import CardComponent from './PiacComponents/CardComponent';
import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";

// Utils
import { formatPhoneNumber } from "../../Utils/PhoneUtils";

function ProducerDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [producer, setProducer] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { config, environment } = require('../../index');

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchData() {
      try {
        const [profileRes, productsRes] = await Promise.all([
          Axios.get(config[environment].getProfileInfoEndpoint(id), { cancelToken: source.token }),
          Axios.get(`${config[environment].ownProduceEndpoint}?producer=${id}`, { cancelToken: source.token })
        ]);
        
        setProducer(profileRes.data);
        const fetchedProducts = productsRes.data.map(p => ({ ...p, key: 'Sajattermeny', picture1: p.picture }));
        setProducts(fetchedProducts);
        
        // Extract unique categories from products
        const uniqueCategories = ['All', ...new Set(fetchedProducts.map(p => p.producer_category_name).filter(Boolean))];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error("Error fetching producer data:", error);
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => source.cancel();
  }, [id, config, environment]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!producer) return <Typography>{t('Market.ProducerNotFound', 'Producer not found')}</Typography>;

  const filteredProducts = tabValue === 0 
    ? products 
    : products.filter(p => p.producer_category_name === categories[tabValue]);

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Button onClick={() => navigate('/termelok')} sx={{ mb: 2 }}>{t('Common.Back')}</Button>
      
      <Card sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar 
              src={producer.profile_picture || defaultProfilePicture} 
              sx={{ width: 120, height: 120, border: '3px solid #105b79' }}
            />
          </Grid>
          <Grid item xs={12} sm>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#105b79' }}>
              {producer.company_name || producer.seller_username}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {producer.bio}
            </Typography>
            {producer.phone_number && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                {t('Administration.PhoneNumber')}: {formatPhoneNumber(producer.phone_number)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Card>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>{t('Market.ProductRange', 'Termékkínálat')}</Typography>
      
      {categories.length > 1 && (
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          {categories.map((cat, idx) => (
            <Tab key={idx} label={cat === 'All' ? t('Map.All') : cat} />
          ))}
        </Tabs>
      )}

      <Grid container spacing={3}>
        {filteredProducts.length === 0 ? (
          <Typography sx={{ p: 3, width: '100%', textAlign: 'center' }}>{t('Market.NoProducts', 'Nincsenek termékek ebben a kategóriában.')}</Typography>
        ) : (
          filteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <CardComponent 
                listing={product} 
                state={{ mapInstance: null }} 
                setFlyToLocation={() => {}} 
                navigate={navigate} 
              />
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default ProducerDetail;
